import {Component, computed, DestroyRef, effect, inject, input, output, signal} from '@angular/core';
import {DecimalPipe} from '@angular/common';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {
  CustomerProductFeatureItem,
  CustomerProductSummary,
  LicencePlanService,
  PricePeriodSummary,
  ProductService,
  ProductType
} from '../../../../../../../api';
import {QueryKey} from '../../../../../../constants/query-key';
import {CustomerProductFeatureFormModel, CustomerProductFormModel} from '../../models/customer-product-form-model';
import {ProductDropdown} from '../../../../../products/components/product-dropdown/product-dropdown';
import {ElciaDatePicker} from '../../../../../../components/date-picker/date-picker';
import {NumericInput} from '../../../../../../components/numeric-input/numeric-input';
import {TextArea} from '../../../../../../components/text-area/text-area';
import {ToggleSwitch} from '../../../../../../components/toggle-switch/toggle-switch';
import {ElciaButton} from '../../../../../../components/buttons/button/button';
import {Label} from '../../../../../../components/label/label';
import {Card2Directive} from '../../../../../../directives/card2.directive';
import {CanAccessFinances} from '../../../../../../directives/can-access-finances';

export interface CustomerProductFormValue {
  productId: number;
  quantity: number;
  startDate: Date | null;
  endDate: Date | null;
  price: number;
  mustIncludeInInvoicing: boolean;
  description: string | null;
  isActive: boolean;
  features: CustomerProductFeatureItem[];
}

@Component({
  selector: 'elcia-customer-product-form',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    DecimalPipe,
    ProductDropdown,
    ElciaDatePicker,
    NumericInput,
    TextArea,
    ToggleSwitch,
    ElciaButton,
    Label,
    Card2Directive,
    CanAccessFinances,
  ],
  templateUrl: './customer-product-form.html',
  styleUrl: './customer-product-form.scss',
})
export class CustomerProductForm {
  initialValue = input<CustomerProductSummary | null>(null);
  isEdit = input<boolean>(false);
  onSubmit = output<CustomerProductFormValue>();

  protected readonly featureProductType = ProductType.FEATURE;
  // The main dropdown only offers licences, pilotages (modules) and plugins — the products billable to a customer.
  protected readonly mainProductTypes = [ProductType.LICENCE, ProductType.MODULE, ProductType.PLUGIN, ProductType.MAINTENANCECONTRACT];

  private readonly productService = inject(ProductService);
  private readonly licencePlanService = inject(LicencePlanService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly productsQuery = injectQuery(() => ({
    queryKey: [QueryKey.PRODUCT_SELECT],
    queryFn: () => lastValueFrom(this.productService.getProductSelect()),
  }));

  protected readonly form: CustomerProductFormModel = new FormGroup({
    productId: new FormControl<number | null>(null, Validators.required),
    quantity: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
    startDate: new FormControl<Date | null>(null),
    endDate: new FormControl<Date | null>(null),
    price: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    mustIncludeInInvoicing: new FormControl<boolean>(true, {nonNullable: true}),
    description: new FormControl<string | null>(null),
    isActive: new FormControl<boolean>(true, {nonNullable: true}),
    features: new FormArray<CustomerProductFeatureFormModel>([]),
  });

  // Set while the form is loaded from initialValue: the productId change it emits is not a user pick.
  private isResetting = false;

  private readonly onInitialValueChange = effect(() => {
    const product = this.initialValue();
    this.form.controls.features.clear();
    this.pendingPrefills.set([]);

    if (!product) {
      this.resetWithoutPrefill({quantity: 1, mustIncludeInInvoicing: true, isActive: true});
      return;
    }

    this.resetWithoutPrefill({
      productId: product.productId,
      quantity: product.quantity,
      startDate: product.startDate ?? null,
      endDate: product.endDate ?? null,
      price: product.price,
      mustIncludeInInvoicing: product.mustIncludeInInvoicing,
      description: product.description ?? null,
      isActive: product.isActive,
    });

    // Push après le reset : reset() réinitialiserait sinon les FormControl nullable de chaque feature à null.
    for (const feature of product.extraFeatures) {
      this.form.controls.features.push(this.buildFeatureGroup(feature));
    }
  });

  private readonly selectedProductId = toSignal(this.form.controls.productId.valueChanges, {
    initialValue: this.form.controls.productId.value,
  });

  // Type du produit sélectionné, ou null tant que la liste n'est pas chargée / produit introuvable.
  private readonly selectedProductType = computed(() => {
    const id = this.selectedProductId();
    const products = this.productsQuery.data()?.products;
    if (id == null || !products) return null;
    return products.find(p => p.id === id)?.productType ?? null;
  });

  // Les features supplémentaires ne concernent que les produits de type licence.
  protected readonly isLicenceProductSelected = computed(() => this.selectedProductType() === ProductType.LICENCE);

  // Fréquence du produit sélectionné, ou null tant que la liste n'est pas chargée / produit introuvable.
  private readonly selectedProductFrequency = computed(() => {
    const id = this.selectedProductId();
    const products = this.productsQuery.data()?.products;
    if (id == null || !products) return null;
    return products.find(p => p.id === id)?.frequency ?? null;
  });

  // A one-shot product (no billing frequency) is invoiced once and has no validity period:
  // its date fields are hidden and forced empty (the backend rejects a one-shot that carries dates).
  protected readonly isOneShotProduct = computed(() => {
    const id = this.selectedProductId();
    const products = this.productsQuery.data()?.products;
    if (id == null || !products) return false;
    const product = products.find(p => p.id === id);
    return product != null && product.frequency == null;
  });

  // A recurring product requires a start date; the end date stays optional so an open-ended
  // subscription keeps being billed at its frequency. A one-shot product has its dates cleared.
  private readonly applyDateRules = effect(() => {
    const hasFrequency = this.selectedProductFrequency() != null;
    const {startDate, endDate} = this.form.controls;

    if (hasFrequency) {
      startDate.setValidators(Validators.required);
    } else {
      startDate.clearValidators();
    }
    startDate.updateValueAndValidity({emitEvent: false});

    // End date is never required: leaving it empty means the product runs indefinitely.
    endDate.clearValidators();
    endDate.updateValueAndValidity({emitEvent: false});

    if (this.isOneShotProduct()) {
      startDate.setValue(null, {emitEvent: false});
      endDate.setValue(null, {emitEvent: false});
    }
  });

  // Purge les features uniquement quand le type est connu et n'est pas une licence (évite d'effacer pendant le chargement).
  private readonly clearFeaturesWhenNotLicence = effect(() => {
    const type = this.selectedProductType();
    if (type != null && type !== ProductType.LICENCE && this.form.controls.features.length > 0) {
      this.form.controls.features.clear();
    }
  });

  // --- Amount preview (unit price × quantity) ---

  // Valeur réactive du formulaire : recalcule les montants à chaque changement (produit, quantité, prix, features).
  private readonly formValue = toSignal(this.form.valueChanges, {initialValue: this.form.getRawValue()});

  // A feature can only be invoiced if the parent licence is itself invoiced.
  // Depends on the main toggle value and on the features array (length/content) so newly added features
  // (addFeature) inherit the state immediately: forced to false and disabled when the main is excluded.
  private readonly syncFeatureInvoicingWithMain = effect(() => {
    const mainIncluded = this.formValue().mustIncludeInInvoicing;
    const features = this.formValue().features ?? [];

    features.forEach((_, index) => {
      const control = this.form.controls.features.at(index).controls.mustIncludeInInvoicing;
      if (mainIncluded) {
        if (control.disabled) {
          control.enable({emitEvent: false});
        }
      } else {
        control.setValue(false, {emitEvent: false});
        if (control.enabled) {
          control.disable({emitEvent: false});
        }
      }
    });
  });

  // Ids des produits sélectionnés (principal + features) dont il faut charger les périodes de prix.
  private readonly selectedProductIds = computed(() => {
    const value = this.formValue();
    const ids = new Set<number>();
    if (value.productId != null) ids.add(value.productId);
    for (const feature of value.features ?? []) {
      if (feature.productId != null) ids.add(feature.productId);
    }
    return [...ids].sort((a, b) => a - b);
  });

  // Détails (avec périodes de prix) de tous les produits sélectionnés, en une seule requête.
  private readonly productDetailsQuery = injectQuery(() => ({
    queryKey: [QueryKey.PRODUCT_DETAILS, this.selectedProductIds()],
    queryFn: () => Promise.all(
      this.selectedProductIds().map(id => lastValueFrom(this.productService.getProductById(id))),
    ),
    enabled: this.selectedProductIds().length > 0,
  }));

  private readonly priceByProductId = computed(() => {
    const map = new Map<number, PricePeriodSummary[]>();
    for (const response of this.productDetailsQuery.data() ?? []) {
      map.set(response.product.id, response.product.pricePeriods);
    }
    return map;
  });

  // Price controls waiting for the catalogue price of the product just picked: product details load asynchronously.
  private readonly pendingPrefills = signal<ReadonlyArray<{productId: number; price: FormControl<number | null>}>>([]);

  private readonly applyPendingPrefills = effect(() => {
    const prices = this.priceByProductId();
    const pending = this.pendingPrefills();
    const ready = pending.filter(prefill => prices.has(prefill.productId));
    if (ready.length === 0) return;

    for (const prefill of ready) {
      prefill.price.setValue(cataloguePriceAt(prices.get(prefill.productId)!, new Date()));
    }
    this.pendingPrefills.set(pending.filter(prefill => !prices.has(prefill.productId)));
  });

  constructor() {
    this.watchPrefill(this.form.controls.productId, this.form.controls.price);
  }

  // Every product pick prefills its price, in edit mode too: a saved price only belongs to its product.
  // A price typed by hand cancels the pending prefill, so a late catalogue response never overwrites it.
  private watchPrefill(productId: FormControl<number | null>, price: FormControl<number | null>): void {
    productId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(id => {
        if (!this.isResetting) this.requestPrefill(id, price);
      });

    price.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.requestPrefill(null, price));
  }

  // A newer pick on the same control replaces the pending one, so a late response never wins.
  // update() rather than a read: this also runs inside the initialValue effect (reset emits), which must not
  // start depending on pendingPrefills.
  private requestPrefill(productId: number | null, price: FormControl<number | null>): void {
    this.pendingPrefills.update(pending => {
      const others = pending.filter(prefill => prefill.price !== price);
      return productId == null ? others : [...others, {productId, price}];
    });
  }

  private resetWithoutPrefill(value: Parameters<CustomerProductFormModel['reset']>[0]): void {
    this.isResetting = true;
    this.form.reset(value);
    this.isResetting = false;
  }

  // --- Features already included in the base licence (greyed out when adding a feature) ---

  // licencePlanId of the main product, read from the details already loaded (productDetailsQuery).
  private readonly selectedLicencePlanId = computed(() => {
    const id = this.selectedProductId();
    if (id == null) return null;
    const main = this.productDetailsQuery.data()?.find(response => response.product.id === id)?.product;
    return main?.licencePlanId ?? null;
  });

  // Licence plan details: provides the FeatureIds included in the base plan.
  private readonly licencePlanQuery = injectQuery(() => ({
    queryKey: [QueryKey.LICENCE_PLAN, this.selectedLicencePlanId()],
    queryFn: () => lastValueFrom(this.licencePlanService.getLicencePlanById(this.selectedLicencePlanId()!)),
    enabled: this.selectedLicencePlanId() != null,
  }));

  private readonly includedFeatureCodes = computed(
    () => new Set(this.licencePlanQuery.data()?.licencePlan.includedFeatureCodes ?? []),
  );

  // Ids of feature-products already covered by the licence → disabled in the feature dropdowns.
  protected readonly disabledFeatureProductIds = computed(() => {
    const codes = this.includedFeatureCodes();
    if (codes.size === 0) return new Set<number>();
    return new Set(
      (this.productsQuery.data()?.products ?? [])
        .filter(product => product.featureId != null && codes.has(product.featureId))
        .map(product => product.id),
    );
  });

  protected readonly mainAmount = computed(() =>
    computeAmount(this.formValue().price ?? null, this.formValue().quantity ?? null),
  );

  protected readonly featureAmounts = computed(() =>
    (this.formValue().features ?? []).map(feature => computeAmount(feature.price ?? null, feature.quantity ?? null)),
  );

  protected get features(): FormArray<CustomerProductFeatureFormModel> {
    return this.form.controls.features;
  }

  protected addFeature(): void {
    this.features.push(this.buildFeatureGroup());
  }

  protected removeFeature(index: number): void {
    this.features.removeAt(index);
  }

  public submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    this.onSubmit.emit({
      productId: raw.productId!,
      quantity: raw.quantity!,
      startDate: raw.startDate,
      endDate: raw.endDate,
      price: raw.price!,
      mustIncludeInInvoicing: raw.mustIncludeInInvoicing,
      description: raw.description,
      isActive: raw.isActive,
      features: raw.features.map(feature => ({
        productId: feature.productId!,
        quantity: feature.quantity!,
        price: feature.price!,
        mustIncludeInInvoicing: feature.mustIncludeInInvoicing,
        description: feature.description,
      })),
    });
  }

  private buildFeatureGroup(feature?: CustomerProductSummary): CustomerProductFeatureFormModel {
    const group: CustomerProductFeatureFormModel = new FormGroup({
      productId: new FormControl<number | null>(feature?.productId ?? null, Validators.required),
      quantity: new FormControl<number | null>(feature?.quantity ?? 1, [Validators.required, Validators.min(1)]),
      price: new FormControl<number | null>(feature?.price ?? null, [Validators.required, Validators.min(0)]),
      mustIncludeInInvoicing: new FormControl<boolean>(feature?.mustIncludeInInvoicing ?? true, {nonNullable: true}),
      description: new FormControl<string | null>(feature?.description ?? null),
    });

    this.watchPrefill(group.controls.productId, group.controls.price);

    return group;
  }
}

// Catalogue price in force on the given day, or null when no period covers it.
function cataloguePriceAt(periods: PricePeriodSummary[], date: Date): number | null {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return periods.find(period =>
    new Date(period.startDate) <= day && (period.endDate == null || day <= new Date(period.endDate)),
  )?.price ?? null;
}

function computeAmount(price: number | null, quantity: number | null): number | null {
  return price == null || quantity == null ? null : price * quantity;
}
